<template>
  <WdsModal :show="show" :title="title" @cancel="close">
    <div>
      <WdsText v-if="status === 'open'">
        {{ $t('Are you sure you want to reopen this project?') }}
      </WdsText>
      <WdsText v-if="status === 'end'">
        {{
          $t(
            'You\'re about to end the project ":project", which is associated with :jobs jobs. Once a project is ended, it will not be available anymore to associate other jobs with it. Do you want to proceed?',
            { project: project.name, jobs: project?.jobs?.paginatorInfo.total },
          )
        }}
      </WdsText>
      <WdsText v-if="status === 'delete'">
        {{
          $t(
            'You\'re about to delete the project ":project", which is associated with :jobs jobs. This action is not reversible. Do you want to proceed?',
            { project: project.name, jobs: project?.jobs?.paginatorInfo.total },
          )
        }}
      </WdsText>
    </div>
    <WdsStack class="tw-mt-5" justify-space-between>
      <WdsButton tertiary @click="close">{{ $t('Cancel') }}</WdsButton>
      <WdsStack gap-sm align-center>
        <WdsSpinner v-if="loading" indeterminate />
        <WdsButton primary :disabled="loading" @click="submit">{{ $t('Confirm') }}</WdsButton>
      </WdsStack>
    </WdsStack>
  </WdsModal>
</template>

<script lang="ts" setup>
import { WdsButton, WdsText, WdsSpinner, WdsStack, WdsModal } from '@worksome/wds-components'
import gql from 'graphql-tag'
import { ref } from 'vue'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { NetworkError } from '@/utils/notification'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps({
  project: {
    required: true,
    type: Object,
  },
  status: {
    required: true,
    type: String,
    validator(val: string) {
      return ['open', 'end', 'delete'].includes(val)
    },
  },
})

const emit = defineEmits(['close', 'submitted', 'deleted', 'ended'])

const show = ref(true)
const loading = ref(false)

const title = (() => {
  switch (props.status) {
    case 'open':
      return $t('Reopen project')
    case 'end':
      return $t('End project')
    case 'delete':
      return $t('Delete project')
    default:
      return props.status // Something went wrong
  }
})()

function close() {
  show.value = false
  setTimeout(() => emit('close'), 150)
}

async function submit() {
  // Submitting
  loading.value = true
  try {
    // Submit query here
    switch (props.status) {
      case 'open':
        await publicApi.mutate({
          mutation: gql`
            mutation openProject($input: OpenProjectInput!) {
              openProject(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: {
              id: props.project.id,
            },
          },
        })
        break
      case 'end':
        await publicApi.mutate({
          mutation: gql`
            mutation endProject($input: EndProjectInput!) {
              endProject(input: $input) {
                id
                endDate
              }
            }
          `,
          variables: {
            input: {
              id: props.project.id,
            },
          },
        })
        break
      case 'delete':
        await publicApi.mutate({
          mutation: gql`
            mutation endProject($input: DeleteProjectInput!) {
              deleteProject(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: {
              id: props.project.id,
            },
          },
        })
        emit('deleted')
        break
      default:
        break
    }
    emit('submitted')
    close()
  } catch (ex: any) {
    new NetworkError({
      msg: $t('Error occurred. The action was not completed'),
      ex,
    }).dispatch()
    loading.value = false
  }
}
</script>
